import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '@/api/client'
import type { Company, Source } from '@/api/types'
import { Button, buttonClasses } from '@/components/ui/Button'
import {
  Badge,
  CompanyStatusBadge,
  ErrorBox,
  Modal,
  PresenceBadges,
  RunStatusBadge,
  Select,
  SourceStatusBadge,
} from '@/components/ui/Primitives'
import { errorMessage, toast } from '@/components/ui/Toast'
import { CompanyFormModal } from '@/features/companies/CompanyFormModal'
import { PreviewPanel } from '@/features/companies/PreviewPanel'
import {
  ATS_LABEL,
  INTERVALS,
  MARKET_FLAG,
  formatDateTime,
  formatDuration,
  formatNumber,
  hostOf,
  relativeTime,
} from '@/lib/utils'

export function CompanyDetailPage() {
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const company = useQuery({
    queryKey: ['companies', id],
    queryFn: () => api.company(id),
    refetchInterval: (q) => (q.state.data?.is_checking || q.state.data?.status === 'pending' ? 3000 : 20_000),
  })
  const busy = Boolean(company.data?.is_checking || company.data?.status === 'pending')
  const pages = useQuery({ queryKey: ['companies', id, 'pages'], queryFn: () => api.careerPages(id), refetchInterval: busy ? 3000 : 20_000 })
  const runs = useQuery({ queryKey: ['companies', id, 'runs'], queryFn: () => api.companyRuns(id), refetchInterval: busy ? 3000 : 20_000 })
  const newJobs = useQuery({
    queryKey: ['jobs', { company_id: id, new_only: true, found_within_days: 30 }],
    queryFn: () => api.jobs({ company_id: id, new_only: true, found_within_days: 30, sort: 'recent', page_size: 8 }),
    refetchInterval: busy ? 5000 : 30_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['companies'] })
    void qc.invalidateQueries({ queryKey: ['stats'] })
  }
  const check = useMutation({
    mutationFn: (full: boolean) => api.checkCompany(id, full),
    onSuccess: (_c, full) => {
      toast.success(full ? 'Full re-check queued' : 'Check queued')
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const schedule = useMutation({
    mutationFn: (body: Partial<Pick<Company, 'scheduler_enabled' | 'crawl_interval_hours'>>) => api.updateCompany(id, body),
    onSuccess: (c) => {
      toast.success(c.scheduler_enabled ? `Scheduler on — next check ${relativeTime(c.next_check_at)}` : 'Scheduler off')
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const remove = useMutation({
    mutationFn: () => api.deleteCompany(id),
    onSuccess: () => {
      toast.success('Company, its career pages and jobs deleted')
      refresh()
      navigate('/companies')
    },
    onError: (e) => toast.error(errorMessage(e)),
  })

  if (company.isError) return <ErrorBox error={company.error} onRetry={() => void company.refetch()} />
  const c = company.data
  if (!c) return <div className="skeleton h-40" />

  return (
    <>
      <Link to="/companies" className="mb-4 inline-block text-[13px] text-ink-muted hover:text-ink">
        ← Companies
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[28px] font-semibold tracking-tight">{c.name}</h1>
            <CompanyStatusBadge status={c.status} checking={c.is_checking} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13.5px] text-ink-muted">
            <PresenceBadges markets={c.markets} />
            {c.industry && <span>· {c.industry}</span>}
            {c.headquarters && <span>· HQ {c.headquarters}</span>}
            {c.website && (
              <a href={c.website} target="_blank" rel="noreferrer" className="link">
                · {hostOf(c.website)}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button loading={check.isPending && !check.variables} disabled={busy || !c.scheduler_enabled || !c.sources_count} onClick={() => check.mutate(false)}>
            {c.is_checking ? 'Checking…' : 'Check now'}
          </Button>
          <Button variant="secondary" disabled={busy || !c.scheduler_enabled || !c.sources_count} loading={check.isPending && check.variables} onClick={() => check.mutate(true)} title="Re-read every posting's details, not only new ones">
            Full re-check
          </Button>
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <Button variant="ghost" className="text-danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </div>
      </div>

      {c.status === 'failing' && c.last_error && (
        <div className="mb-5 rounded-field border border-danger/25 bg-danger/5 px-4 py-3 text-[13.5px] text-danger">
          <b className="font-medium">Last check failed:</b> {c.last_error}
        </div>
      )}

      <div className="mb-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        {/* Scheduler */}
        <section className="card p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-[15px] font-semibold">Scheduler</h2>
            <label className="flex cursor-pointer items-center gap-2 text-[13.5px]">
              <span className={c.scheduler_enabled ? 'font-medium text-[#1f7a4d]' : 'text-ink-muted'}>{c.scheduler_enabled ? 'On' : 'Off'}</span>
              <input
                type="checkbox"
                role="switch"
                className="h-4 w-8 cursor-pointer accent-ink"
                checked={c.scheduler_enabled}
                disabled={schedule.isPending}
                onChange={(e) => schedule.mutate({ scheduler_enabled: e.target.checked })}
              />
            </label>
          </div>
          <div className="mb-4 flex items-center gap-3">
            <span className="text-[13.5px] text-ink-soft">Check for new openings</span>
            <Select
              value={c.crawl_interval_hours}
              disabled={schedule.isPending}
              onChange={(e) => schedule.mutate({ crawl_interval_hours: Number(e.target.value) })}
              className="w-auto"
            >
              {INTERVALS.map(([h, label]) => (
                <option key={h} value={h}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
            <Fact label="Last checked" value={relativeTime(c.last_checked_at)} sub={formatDateTime(c.last_checked_at)} />
            <Fact
              label="Next check"
              value={!c.scheduler_enabled ? 'Off' : c.next_check_at ? relativeTime(c.next_check_at) : c.sources_count ? 'queued' : '—'}
              sub={c.scheduler_enabled ? formatDateTime(c.next_check_at) : undefined}
            />
            <Fact label="Last new opening" value={c.last_new_job_at ? relativeTime(c.last_new_job_at) : 'None yet'} sub={c.last_new_job_at ? formatDateTime(c.last_new_job_at) : 'first check is the baseline'} />
            <Fact label="Last successful check" value={relativeTime(c.last_success_at)} />
          </dl>
        </section>

        {/* Numbers */}
        <section className="card grid grid-cols-2 gap-4 p-5">
          <Big label="Open jobs" value={formatNumber(c.jobs_active)} to={`/jobs?company_id=${c.id}`} />
          <Big label="New · last 7 days" value={`+${formatNumber(c.new_jobs_7d)}`} tone="success" to={`/jobs?company_id=${c.id}&new_only=true&found_within_days=7&sort=recent`} />
          <Big label={`${MARKET_FLAG.IN} India`} value={formatNumber(c.jobs_india)} to={`/jobs?company_id=${c.id}&market=IN`} />
          <Big label={`${MARKET_FLAG.US} USA`} value={formatNumber(c.jobs_us)} to={`/jobs?company_id=${c.id}&market=US`} />
        </section>
      </div>

      <CareerPages company={c} pages={pages.data ?? []} />

      <section className="card mb-6 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4">
          <h2 className="text-[15px] font-semibold">New openings · last 30 days</h2>
          <Link to={`/jobs?company_id=${c.id}&new_only=true&sort=recent`} className="link text-[13px]">
            All new openings
          </Link>
        </div>
        {newJobs.data?.items.length ? (
          <ul>
            {newJobs.data.items.map((job) => (
              <li key={job.id} className="flex items-baseline gap-3 border-t border-line px-5 py-2.5 text-[13.5px]">
                <Link to={`/jobs?company_id=${c.id}&job=${job.id}`} className="min-w-0 flex-1 truncate font-medium hover:underline">
                  {job.title}
                </Link>
                <span className="hidden truncate text-ink-muted sm:block sm:max-w-[40%]">{job.location}</span>
                <span className="w-28 shrink-0 text-right text-[12.5px] text-[#1f7a4d]">found {relativeTime(job.first_seen_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="border-t border-line px-5 py-6 text-[13.5px] text-ink-muted">
            {c.last_checked_at
              ? 'No new openings since the first check. The first check records what is already open as the baseline; anything posted after it shows here.'
              : 'Not checked yet.'}
          </p>
        )}
      </section>

      <section className="card overflow-hidden">
        <h2 className="px-5 py-4 text-[15px] font-semibold">Check history</h2>
        <div className="overflow-x-auto">
          <table className="table-base min-w-[900px]">
            <thead>
              <tr>
                <th>When</th>
                <th>Result</th>
                <th>Career page</th>
                <th className="text-right">Open</th>
                <th className="text-right">New</th>
                <th className="text-right">Closed</th>
                <th>Took</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.map((run) => (
                <tr key={run.id}>
                  <td className="whitespace-nowrap">
                    {formatDateTime(run.started_at)}
                    <span className="block text-[12px] text-ink-faint">{run.trigger === 'manual' ? 'manual' : 'scheduled'}</span>
                  </td>
                  <td>
                    <RunStatusBadge status={run.status} />
                  </td>
                  <td className="max-w-[200px] truncate text-[12.5px] text-ink-muted">{hostOf(run.source_url)}</td>
                  <td className="text-right tabular-nums">{formatNumber(run.jobs_found)}</td>
                  <td className="text-right tabular-nums text-[#1f7a4d]">{run.jobs_new ? `+${formatNumber(run.jobs_new)}` : '0'}</td>
                  <td className="text-right tabular-nums">{formatNumber(run.jobs_closed)}</td>
                  <td className="whitespace-nowrap">{formatDuration(run.duration_ms)}</td>
                  <td className="max-w-[360px] text-[12.5px]">
                    {run.error && <p className="text-danger">{run.error}</p>}
                    {run.notes && <p className="text-ink-muted">{run.notes}</p>}
                  </td>
                </tr>
              ))}
              {runs.data?.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-ink-muted">
                    {c.sources_count ? 'Queued — the first check starts within seconds.' : 'Add a career page to start checking.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing && <CompanyFormModal company={c} onClose={() => setEditing(false)} />}
      {confirmDelete && (
        <Modal title={`Delete ${c.name}?`} onClose={() => setConfirmDelete(false)} width="max-w-md">
          <p className="text-[14px] text-ink-soft">
            The company, its {c.sources_count} career page{c.sources_count === 1 ? '' : 's'} and {formatNumber(c.jobs_active)} open jobs will be deleted. This cannot be undone.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}

function Fact({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div>
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
      {sub && sub !== '—' && <dd className="text-[12px] text-ink-faint">{sub}</dd>}
    </div>
  )
}

function Big({ label, value, to, tone }: { label: string; value: string; to: string; tone?: 'success' }) {
  return (
    <Link to={to} className="group rounded-field p-1">
      <p className="label-eyebrow">{label}</p>
      <p className={`mt-1 text-[26px] font-semibold tabular-nums group-hover:underline ${tone ? 'text-[#1f7a4d]' : ''}`}>{value}</p>
    </Link>
  )
}

function CareerPages({ company, pages }: { company: Company; pages: Source[] }) {
  const qc = useQueryClient()
  const [url, setUrl] = useState('')
  const [adding, setAdding] = useState(false)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['companies'] })
  const preview = useMutation({ mutationFn: () => api.preview(url.trim()) })
  const add = useMutation({
    mutationFn: () => api.addCareerPage(company.id, url.trim()),
    onSuccess: () => {
      toast.success('Career page added — first check starting')
      setUrl('')
      setAdding(false)
      preview.reset()
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const remove = useMutation({
    mutationFn: (sourceId: number) => api.deletePage(sourceId),
    onSuccess: () => {
      toast.success('Career page removed with its jobs')
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const discover = useMutation({
    mutationFn: () => api.discover(company.website ?? ''),
    onSuccess: (d) => {
      if (d.careers_url) {
        setUrl(d.careers_url)
        setAdding(true)
        toast.success(`Found: ${d.how}`)
      } else toast.error('No careers page found on the website')
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (url.trim()) add.mutate()
  }

  return (
    <section className="card mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
        <h2 className="text-[15px] font-semibold">Career pages</h2>
        <div className="flex gap-2">
          {company.website && (
            <Button size="sm" variant="secondary" loading={discover.isPending} onClick={() => discover.mutate()}>
              Find from website
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
            Add career page
          </Button>
        </div>
      </div>
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-t border-line bg-surface-muted/50 px-5 py-4">
          <div className="flex gap-2">
            <input
              className="field-box"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value)
                preview.reset()
              }}
              placeholder="https://careers.company.com or a job board link"
              autoFocus
            />
            <Button variant="secondary" disabled={!url.trim()} loading={preview.isPending} onClick={() => preview.mutate()}>
              Test
            </Button>
            <Button type="submit" disabled={!url.trim()} loading={add.isPending}>
              Add
            </Button>
          </div>
          {preview.isError && <ErrorBox error={preview.error} />}
          {preview.data && <PreviewPanel preview={preview.data} />}
        </form>
      )}
      {pages.length === 0 ? (
        <p className="border-t border-line px-5 py-6 text-[13.5px] text-ink-muted">
          No career page yet — add its careers URL (or use "Find from website") and the scheduler starts checking it.
        </p>
      ) : (
        <ul>
          {pages.map((page) => (
            <li key={page.id} className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3 text-[13.5px]">
              <Badge tone={page.ats_type === 'unknown' ? 'neutral' : 'info'}>{ATS_LABEL[page.ats_type]}</Badge>
              <a href={page.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">
                {page.url.replace(/^https?:\/\//, '')}
              </a>
              <SourceStatusBadge status={page.status} crawling={page.is_crawling} />
              <span className="w-20 text-right tabular-nums">{formatNumber(page.jobs_active)} jobs</span>
              <span className="w-28 text-right text-[12.5px] text-ink-muted">{relativeTime(page.last_crawled_at)}</span>
              <Link to={`/jobs?source_id=${page.id}`} className={buttonClasses('ghost', 'sm')}>
                Jobs
              </Link>
              <Button size="sm" variant="ghost" className="text-danger" loading={remove.isPending && remove.variables === page.id} onClick={() => remove.mutate(page.id)}>
                Remove
              </Button>
              {page.last_error && <p className="w-full text-[12.5px] text-danger">{page.last_error}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
